/**
 * Attivita di tipo misurazione: la definizione dei campi, in un posto solo.
 *
 * Una attivita normale e una spunta: fatta o da fare. Una attivita di misurazione chiede
 * invece dei numeri, e quali numeri lo decide il catalogo: "Controllo cloro e pH" vuole il
 * pH senza unita e il cloro in ppm, un controllo di pressione ne vorrebbe altri. La forma
 * dei campi vive qui e non in due copie fra client e server, che e la lezione delle unita
 * di misura in `material-units.ts`.
 *
 * Gli intervalli di riferimento stanno **sulla definizione del campo**, non sul valore
 * misurato: cambiarli rilegge anche lo storico con il metro di oggi, e il numero letto con
 * lo strumento in mano non si tocca mai. E la stessa scelta del registro piscine, e il
 * giudizio "fuori range" lo da la stessa funzione, `verdictFor` in `pool-thresholds.ts`:
 * due calcoli separati sullo stesso numero prima o poi direbbero cose diverse.
 */
import { verdictFor, type PoolRange, type ReadingVerdict } from "./pool-thresholds";
import { normalizeSearchText } from "./search";

export type MeasurementField = {
  /** Chiave stabile del campo: resta uguale anche se il nome mostrato cambia. */
  key: string;
  label: string;
  /** Unita di misura scritta come la dice chi lavora, `ppm` o `bar`; vuota per il pH. */
  unit: string | null;
  min: number | null;
  max: number | null;
};

/** Piu di cosi non e una misurazione da campo, e un modulo da compilare. */
export const MEASUREMENT_FIELD_LIMIT = 8;

/** Il numero scritto a mano: virgola o punto, e il campo vuoto vale "non misurato". */
export function parseDecimalInput(value: string): number | null {
  const pulito = value.trim().replace(",", ".");
  if (!pulito) return null;
  const numero = Number(pulito);
  return Number.isFinite(numero) ? numero : null;
}

/** Chiave stabile ricavata dal nome, quando chi configura non ne indica una. */
export function measurementFieldKey(label: string): string {
  const base = normalizeSearchText(label)
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return base || "valore";
}

/**
 * I campi come arrivano dal database, ripuliti e resi affidabili.
 *
 * Il jsonb puo contenere qualsiasi cosa, comprese righe scritte da una versione futura o da
 * una mano sbagliata: qui si scarta quello che non e un campo valido invece di far esplodere
 * la schermata di chi sta lavorando sul posto.
 */
export function normalizeMeasurementFields(raw: unknown): MeasurementField[] {
  if (!Array.isArray(raw)) return [];
  const campi: MeasurementField[] = [];
  const viste = new Set<string>();
  for (const voce of raw) {
    if (!voce || typeof voce !== "object") continue;
    const record = voce as Record<string, unknown>;
    const label = typeof record.label === "string" ? record.label.trim() : "";
    if (!label) continue;
    const chiave = typeof record.key === "string" && record.key.trim() ? record.key.trim() : measurementFieldKey(label);
    if (viste.has(chiave)) continue;
    viste.add(chiave);
    const unit = typeof record.unit === "string" && record.unit.trim() ? record.unit.trim() : null;
    const min = typeof record.min === "number" && Number.isFinite(record.min) ? record.min : null;
    const max = typeof record.max === "number" && Number.isFinite(record.max) ? record.max : null;
    campi.push({ key: chiave, label, unit, min, max });
    if (campi.length === MEASUREMENT_FIELD_LIMIT) break;
  }
  return campi;
}

/** L'intervallo consigliato del campo, quando c'e: serve entrambi gli estremi per avere senso. */
export function measurementRange(field: Pick<MeasurementField, "min" | "max">): PoolRange | null {
  if (field.min == null || field.max == null) return null;
  if (field.max <= field.min) return null;
  return { min: field.min, max: field.max };
}

/** Il giudizio su un valore misurato: senza intervallo dichiarato non c'e niente da giudicare. */
export function measurementVerdict(
  field: Pick<MeasurementField, "min" | "max">,
  value: number | null | undefined,
): ReadingVerdict {
  const range = measurementRange(field);
  if (!range) return value == null ? "non-misurato" : "dentro";
  return verdictFor(value, range);
}

/** Il numero come lo scrive una persona in italiano, con la sua unita quando ce l'ha. */
export function formatMeasurementValue(value: number | null, unit: string | null): string {
  if (value == null) return "non misurato";
  const numero = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 3, useGrouping: false }).format(value);
  return unit ? `${numero} ${unit}` : numero;
}

/** L'intervallo scritto per il segnaposto del campo: "da 7,2 a 7,6". */
export function formatMeasurementRange(field: Pick<MeasurementField, "min" | "max" | "unit">): string | null {
  const range = measurementRange(field);
  if (!range) return null;
  return `da ${formatMeasurementValue(range.min, null)} a ${formatMeasurementValue(range.max, field.unit ?? null)}`;
}

/**
 * I campi di "Controllo cloro e pH", il primo caso d'uso chiesto dal cliente.
 *
 * Vivono qui perche li usano in tre: la migrazione che converte l'attivita esistente, la
 * semina dimostrativa e le prove automatiche. Tre copie divergerebbero al primo ritocco.
 */
export const POOL_MEASUREMENT_FIELDS: MeasurementField[] = [
  { key: "ph", label: "pH", unit: null, min: 7.2, max: 7.6 },
  { key: "cloro", label: "Cloro", unit: "ppm", min: 1, max: 1.5 },
];
